import {Collapse} from '@mui/material';
import {
  ConfigObjectType,
  enumContainer,
  ExpandUpButton,
  IconHeaderWithSearch,
  LightIconButton,
  LightLayoutNodeFCProps,
  MaximizeButton,
  omitNullsy,
  useApiQuery,
  useDefaultScrollTo,
  useLocalStorage,
} from '@wallaby-ui/core';
import {
  FILTER_ACTIVE_ICON_COLORS,
  formatLabMethodName,
  LabResultEventTypeV3Dto,
  LightDateRangeFilter,
  LightSavedFiltersButton,
  LightSelectFilter,
  LightSelectedFilters,
  hospitalizationsContainer,
  MerieSchema,
  ShortcutEnum,
} from '@wallaby-ui/document';
import {isEqual} from 'lodash';
import React, {useEffect, useMemo} from 'react';

import {HospitalizationState} from './HospitalizationDetailLayoutV2';
import {useAppSelector} from 'src/js/store_hooks';

export type HospitalizationLabsFilters = {
  dateFrom?: string;
  dateTo?: string;
  floatingShortcutName?: ShortcutEnum | null;
  laboratories?: number[];
  labResultEventTypes?: string[];
};
export const DEFAULT_HOSPITALIZATION_LABS_FILTERS: HospitalizationLabsFilters = {};
export type HospitalizationLabsProps = {
  state: HospitalizationState;
  changeState: (diff: Partial<HospitalizationState>) => void;
  storageIdPrefix: string;
  config: ConfigObjectType;
  encounterId: number | undefined;
  pharmaRecommendations: MerieSchema.components['schemas']['PharmaRecommendationsDto'] | undefined;
  chartEvents: MerieSchema.components['schemas']['EncounterTimelineChartEventsDto'] | undefined;
};
export const HospitalizationDetailLabsV2: React.FC<LightLayoutNodeFCProps & HospitalizationLabsProps> = (props) => {
  const {
    state,
    changeState,
    storageIdPrefix,
    config,
    encounterId,
    minimized,
    expanded,
    maximized,
    setMinimizedMaximized,
    pharmaRecommendations,
    chartEvents,
  } = props;
  const [labsDefaultScroll, setLabsDefaultScroll] = useLocalStorage(`${storageIdPrefix}.labResultsScroll`, 0);
  const scrollRef = useDefaultScrollTo(labsDefaultScroll);
  // data
  const enumMap = useAppSelector((state) => state[enumContainer.ENUM_REDUCER_STATE_ID].enumMap);
  const laboratoriesUrl = `${config.service.url.chartRoot}/supported-event-groups-v3`;
  const {data: laboratoriesFilterIds, isLoading: laboratoriesFilterIdsIsLoading} = useApiQuery<string[]>([laboratoriesUrl], null, {
    axiosConfig: {
      url: laboratoriesUrl,
    },
  });
  const laboratoriesFilterValues = useMemo(() => {
    return (laboratoriesFilterIds ?? []).map((name) => ({
      name,
      messageKey: (enumMap.LabResultEventGroupEnum ?? []).find((v) => v.name === name)?.messageKey,
    }));
  }, [enumMap.LabResultEventGroupEnum, laboratoriesFilterIds]);
  const labResultEventTypesUrl = `${config.service.url.chartRoot}/supported-event-types-v3`;
  const {data: labResultEventTypes, isLoading: labResultEventTypeIdsIsLoading} = useApiQuery<LabResultEventTypeV3Dto[]>(
    [labResultEventTypesUrl],
    null,
    {
      axiosConfig: {
        url: labResultEventTypesUrl,
      },
    }
  );
  const labResultEventTypeValues = useMemo(() => {
    // Hospital-named results (labels that do NOT start with '[') come first, then our technical
    // names (which start with '[<code>]'), each subgroup sorted A→Z.
    return (labResultEventTypes ?? [])
      .map((type) => ({
        name: type.eventType,
        label: formatLabMethodName({eventType: type.eventType, label: type.label, unit: type.unit}),
      }))
      .sort((a, b) => {
        const technicalA = a.label.startsWith('[') ? 1 : 0;
        const technicalB = b.label.startsWith('[') ? 1 : 0;
        if (technicalA !== technicalB) return technicalA - technicalB;
        return a.label.localeCompare(b.label);
      });
  }, [labResultEventTypes]);
  const isBigMode = !minimized || maximized;
  const hasFilters = !isEqual(omitNullsy(state.labCharts), omitNullsy(DEFAULT_HOSPITALIZATION_LABS_FILTERS));

  return (
    <div className='dl-light-box'>
      <IconHeaderWithSearch iconName='laboratoryIcon' label='hospitalization.detail.laboratoryResult'>
        <div className='dl-header-buttons-row' style={{gap: 6}}>
          <LightSelectedFilters
            width='auto'
            hasFilters={hasFilters}
            handleResetFilters={() => {
              changeState({
                labCharts: DEFAULT_HOSPITALIZATION_LABS_FILTERS,
              });
            }}
          />
          <div className='dl-header-icons-row' style={{gap: 6}}>
            <LightIconButton
              iconName='filterIcon'
              useFixedViewBox='0 0 20 16'
              style={{width: 20, height: 16}}
              title={state.areFiltersOpen.labCharts ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
              onClick={() => {
                if (minimized && !state.areFiltersOpen.labCharts) setMinimizedMaximized({minimized: false});
                changeState({areFiltersOpen: {...state.areFiltersOpen, labCharts: !state.areFiltersOpen.labCharts}});
              }}
              selected={state.areFiltersOpen.labCharts}
              colors={hasFilters && !state.areFiltersOpen.labCharts ? FILTER_ACTIVE_ICON_COLORS : undefined}
            />
            <LightSavedFiltersButton
              storageIdPrefix={`${storageIdPrefix}.labCharts`}
              component='hospitalizationLabs'
              config={config}
              filter={state.labCharts}
              setFilter={(newFilter) => changeState({labCharts: newFilter as HospitalizationLabsFilters})}
            />
            <ExpandUpButton expanded={expanded} toggleExpanded={() => setMinimizedMaximized({expanded: !expanded, maximized: false})} />
            <MaximizeButton maximized={maximized} toggleMaximized={() => setMinimizedMaximized({maximized: !maximized})} />
          </div>
        </div>
      </IconHeaderWithSearch>
      {isBigMode && (
        <>
          <Collapse style={{width: '100%', minHeight: 'none'}} in={state.areFiltersOpen.labCharts} timeout={500}>
            <div className='dl-filters-row'>
              <LightDateRangeFilter
                config={{
                  name: 'dateRange',
                  ui: {label: 'hospitalization.detail.chart.dateRange'},
                }}
                value={
                  state.labCharts.dateFrom && state.labCharts.dateTo
                    ? {
                        startDate: state.labCharts.dateFrom,
                        endDate: state.labCharts.dateTo,
                        shortcut: state.labCharts.floatingShortcutName,
                      }
                    : null
                }
                onChange={(newValue) => {
                  changeState({
                    labCharts: {
                      ...state.labCharts,
                      dateFrom: newValue?.startDate,
                      dateTo: newValue?.endDate,
                      floatingShortcutName: newValue?.shortcut,
                    },
                  });
                }}
                dateOnly
                showShortcuts
                isLabResults
              />
              <LightSelectFilter
                config={{
                  name: 'laboratories',
                  ui: {
                    label: 'hospitalization.detail.chart.laboratories',
                  },
                }}
                loading={laboratoriesFilterIdsIsLoading}
                options={laboratoriesFilterValues}
                value={state.labCharts.laboratories}
                onChange={(newValue) => {
                  changeState({
                    labCharts: {
                      ...state.labCharts,
                      laboratories: newValue,
                    },
                  });
                }}
                multi
              />
              <LightSelectFilter
                config={{
                  name: 'labResultEventTypes',
                  ui: {
                    label: 'hospitalization.detail.chart.labResultEventTypes',
                  },
                }}
                loading={labResultEventTypeIdsIsLoading}
                options={labResultEventTypeValues}
                value={state.labCharts.labResultEventTypes}
                onChange={(newValue) => {
                  changeState({
                    labCharts: {
                      ...state.labCharts,
                      labResultEventTypes: newValue,
                    },
                  });
                }}
                multi
              />
            </div>
          </Collapse>
          <div
            style={{flex: 1, overflowY: 'auto'}}
            ref={scrollRef}
            onScroll={(event) => setLabsDefaultScroll((event.target as HTMLElement).scrollTop)}
          >
            <hospitalizationsContainer.DiagnosisLaboratoryResults
              config={config}
              encounterId={encounterId}
              filters={state.labCharts}
              pharmaRecommendations={pharmaRecommendations}
              chartEvents={chartEvents}
            />
          </div>
        </>
      )}
    </div>
  );
};
